import React, { useRef } from "react";
import { BadgeDTO } from "utils/helpers/models/host-dtos/badge.dto";
import { PictureDTO } from "utils/helpers/models/shared/picture.dto";
import { BAGDE_TYPES } from "utils/helpers/enums/shared.enum";
import { warningToaster } from "utils/helpers/common/alert-service";
import useAuth from "containers/auth/useHooks";
import { AiOutlineClose } from "react-icons/ai";
import useOutsideClick from "hooks/click-outside";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onBadgeCreated: () => void;
  createBadge: (badge: BadgeDTO) => Promise<void>;
}

const CreateBadgeModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onBadgeCreated,
  createBadge,
}) => {
  const [formData, setFormData] = React.useState<BadgeDTO>(new BadgeDTO());
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile } = useAuth();
  const modalRef = useOutsideClick(onClose);

  // Cleanup object URL on unmount or when image changes
  React.useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    const file = files[0];
    if (file) {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);

      formData.imageUrl = file.name;
      const picture = await uploadFile(file);
      if (picture) {
        formData.imageId = picture.id;
        formData.image = picture;
      }
      setFormData({ ...formData });
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setFormData((prev) => ({
      ...prev,
      imageUrl: "",
      imageId: 0,
      image: new PictureDTO(),
    }));
    // Reset the file input so the same file can be uploaded again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onSubmitBadge = async () => {
    if (!formData.imageId) {
      warningToaster("Badge image is required");
    } else {
      const badgeData = {
        ...formData,
        badgeType: BAGDE_TYPES.EVENT,
      };
      await createBadge(badgeData);
      onBadgeCreated();
      onClose();
      setFormData(new BadgeDTO());
      setImagePreview(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{ zIndex: 'calc(var(--z-modal) + 1)' }}
    >
      <div
        ref={modalRef}
        className={`relative bg-white px-6 py-8 rounded-lg shadow-lg w-[90%] sm:w-[380px] h-auto transform transition-transform duration-300 ${
          isOpen ? "scale-100" : "scale-95"
        }`}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 transition"
        >
          <AiOutlineClose size={20} />
        </button>

        {/* Modal Title */}
        <h2 className="text-xl font-semibold text-gray-800 text-center mb-6">
          Create New Badge
        </h2>

        <form className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Badge Image *
                </label>
                <div className="relative flex items-center">
                  <label className="bg-gray-200 px-4 py-2 rounded-lg cursor-pointer hover:bg-gray-300 transition-colors">
                    + Upload Image
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleFileChange}
                      accept="image/*"
                    />
                  </label>
                </div>
              </div>

              {imagePreview && (
                <div className="relative w-12 h-12 mt-7">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-full object-cover rounded"
                  />
                  <button
                    type="button"
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                    onClick={handleRemoveImage}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-center mt-6">
            <button
              className="bg-primaryBlueBg text-white px-6 py-2 rounded-lg"
              type="button"
              onClick={onSubmitBadge}
            >
              Save Badge
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBadgeModal;